import { actionChannel, takeLatest, call, put, select, race, take, all, fork } from 'redux-saga/effects';
import _ from 'lodash';

import { CKForm } from 'console-kit';

import RefData from 'ref-data';
import { Experience, Account, Workspace } from 'common-entities';

import LivePreviewUtils from '../../common/components/Experience/RevealPromotions/EditPromotion/PromotionPreview/utils/livePreviewUtils';
import editorSettings from '../../ScreenDesigner/enums/editorSettings';
import designerTypes from '../../common/enums/designerTypes';
import { CTA_OPTIONS } from '../../common/tools/ctaUtils';
import CampaignPromotionActions from './promotion-actions';
import CampaignPromotionsSelectors from './promotion-selectors';
import CampaignPromotionsApi from './promotion-api';
import { editPromotionLayout } from './promotion-legacy-interface';
import {
  campaignPromotionListSortBy,
  SETTINGS_FORM_FIELDS,
  FORM_ID,
  PROMOTION_PANELS,
  ACTION_NAMESPACE,
} from './promotion-enums';
import promotionsModel from './promotion-model';

function* fetchPromotionsHandler() {
  const typeRef = RefData.Enum.TYPE.CAMPAIGNPROMOTION.REVEAL;
  try {
    const experience = yield select(Experience.Selectors.experience);
    const account = yield select(Account.Selectors.activeAccount);
    const containerRef = `campaign:${account.id}:${experience.id}`;
    const promotions = yield call(CampaignPromotionsApi.fetchCampaignPromotions, {
      containerRef,
      typeRef,
      sort:  campaignPromotionListSortBy.CURRENT_FUTURE_PAST,
      order: 'asc',
    });
    yield put(CampaignPromotionActions.fetchPromotionsSuccess(promotions));
  } catch (error) {
    yield put(CampaignPromotionActions.fetchPromotionsFailure(error));
  }
}

function* openPromotionEditorHandler({ payload: { id } }) {
  yield put(CampaignPromotionActions.takePromotionsSnapshot());
  yield put(CampaignPromotionActions.editPromotion(id));
}

function* editPromotionLayoutHandler({ payload: { id } }) {
  const promotion = yield select(CampaignPromotionsSelectors.campaignPromotionById, id);
  const experience = yield select(Experience.Selectors.experience);
  if (experience.isMacroExperience) {
    // wrapping Macro part into in try-catch because editPromotionLayout is legacy interface
    try {
      yield call(editPromotionLayout, promotion);
      yield put(CampaignPromotionActions.editPromotionLayoutSuccess());
    } catch (error) {
      yield put(CampaignPromotionActions.editPromotionLayoutFailure(error));
    }
  } else {
    yield put(CampaignPromotionActions.takePromotionsSnapshot());

    const detailedPromotionsIds = yield select(CampaignPromotionsSelectors.detailedPromotionsIds);
    const activeWorkspace = yield select(Workspace.Selectors.activeWorkspace);
    const canUseForms = activeWorkspace.canUseMechanic(Workspace.Enums.mechanicTypes.ENTRIES);
    yield put(
      Workspace.Actions.launchScreenDesigner({
        editorView:      editorSettings.screenDesignerViews.MODULES,
        activeLayoutId:  promotion.id,
        experienceId:    experience.id,
        themeId:         experience.themeId,
        editedLayoutIds: detailedPromotionsIds,
        editorMode:      editorSettings.modes.PROMOTION,
        displayType:     experience.displayType.group,
        designerType:    designerTypes.PROMOTION,
        canUseForms,
      })
    );
  }
}

function* createPromotionFromTemplateHandler() {
  const experience = yield select(Experience.Selectors.experience);
  const account = yield select(Account.Selectors.activeAccount);
  const containerRef = `campaign:${account.id}:${experience.id}`;
  const promotionTemplate = promotionsModel.create({
    containerRef,
    id:              null,
    targetType:      'target.link.url', // in be-ref-data
    displayStrategy: RefData.Model.hydrate(RefData.Enum.DISPLAY.CAMPAIGNPROMOTION.COMPACT),
    detailStrategy:  RefData.Model.hydrate(RefData.Enum.STRATEGY.PROMOTION_DETAILS.NONE),
    typeRef:         RefData.Model.hydrate(RefData.Enum.TYPE.CAMPAIGNPROMOTION.REVEAL),
    classRef:        RefData.Model.hydrate(RefData.Enum.CLASS.CAMPAIGNPROMOTION.DEFAULT),
    format:          RefData.Model.hydrate(RefData.Enum.FORMAT.CAMPAIGNPROMOTION.CONTENT),
    status:          RefData.Model.hydrate(RefData.Enum.STATUS.CAMPAIGNPROMOTION.DRAFT),
    modifiedBy:      {},
    createdBy:       {},
  });
  yield put(CampaignPromotionActions.takePromotionsSnapshot());
  yield put(CampaignPromotionActions.addPromotionTemplateToTheList(promotionTemplate));
}

// compile a list of errors from the form errors
function* reduceFormErrors(errorsObj) {
  return _.reduce(
    errorsObj,
    (errorsList, v, k) => {
      if (!_.isEmpty(v)) errorsList.push(`${_.startCase(k)}: ${_.head(v)}`);
      return errorsList;
    },
    []
  );
}

function* persistPromotionsHandler({ payload: { saveAndClose } }) {
  yield put(CKForm.Actions.validateForm(FORM_ID));
  const _formValid = yield select(CKForm.Selectors.isFormValid, FORM_ID);

  const _formErrors = yield select(CKForm.Selectors.getFormErrors, FORM_ID);

  const _errorList = yield call(reduceFormErrors, _formErrors);

  if (_formValid) {
    const unsavedPromotions = yield select(CampaignPromotionsSelectors.getUnsavedPromotions);

    let updatedPromotions = [];
    if (unsavedPromotions.length > 0) {
      updatedPromotions = yield call(CampaignPromotionsApi.updateCampaignPromotions, unsavedPromotions);
    }
    if (updatedPromotions.error) yield put(CampaignPromotionActions.persistPromotionsFailure(updatedPromotions.error));
    else
      yield put(
        CampaignPromotionActions.persistPromotionsSuccess(updatedPromotions, 'Promotions Updated', saveAndClose)
      );
  } else {
    if (_.some(SETTINGS_FORM_FIELDS, (field) => !_.isEmpty(_formErrors[field]))) {
      yield put(CampaignPromotionActions.setActiveEditPanel(PROMOTION_PANELS.SETTINGS));
    }

    yield put(CampaignPromotionActions.persistPromotionsFailure({ message: _errorList }));
  }
}

/**
 * This function is used to attempt to edit a promotion.
 * It will check if the promotion is currently being edited and if so, it will validate the form and save the active promotion if it has changed.
 * If the form is not valid, it will display the form errors.
 */
function* attemptEditPromotionHandler({ payload: { id } }) {
  //check if we are currently editing a promotion
  const activePromotion = yield select(CampaignPromotionsSelectors.activePromotion);
  if (!_.isEmpty(activePromotion)) {
    yield put(CKForm.Actions.validateForm(FORM_ID));
    const _formValid = yield select(CKForm.Selectors.isFormValid, FORM_ID);
    if (_formValid) {
      //form is valid, check if we need to save the active promotion
      const promotionsById = yield select(CampaignPromotionsSelectors.campaignPromotionsById);
      const persistedPromotion = promotionsById[activePromotion.id];
      if (!_.isEqual(activePromotion, persistedPromotion)) {
        yield put(CampaignPromotionActions.savePromotion(activePromotion));
      }

      yield put(CampaignPromotionActions.editPromotion(id));
    } else {
      const _formErrors = yield select(CKForm.Selectors.getFormErrors, FORM_ID);
      const _errorList = yield call(reduceFormErrors, _formErrors);
      yield put(CampaignPromotionActions.persistPromotionsFailure({ message: _errorList }));
    }
  }
}

function* publishPromotionHandler({ payload: { id } }) {
  const { promotion, successMessage, error } = yield call(CampaignPromotionsApi.publishCampaignPromotion, id);
  if (error) yield put(CampaignPromotionActions.publishPromotionFailure(error));
  else yield put(CampaignPromotionActions.publishPromotionSuccess(promotion, successMessage));
}

function* draftPromotionHandler({ payload: { id } }) {
  const { promotion, successMessage, error } = yield call(CampaignPromotionsApi.draftCampaignPromotion, id);
  if (error) yield put(CampaignPromotionActions.draftPromotionFailure(error));
  else yield put(CampaignPromotionActions.draftPromotionSuccess(promotion, successMessage));
}

function* showConfirmDeleteMessageHandler({ payload: { id } }) {
  try {
    const { deletePromotion } = yield race({
      deletePromotion: take(CampaignPromotionActions.deletePromotion.getType()),
      abort:           take(CampaignPromotionActions.hideConfirmDeleteMessage.getType()),
    });

    if (deletePromotion) {
      const { successMessage, error } = yield call(CampaignPromotionsApi.deleteCampaignPromotion, id);
      if (error) yield put(CampaignPromotionActions.deletePromotionFailure(error));
      else yield put(CampaignPromotionActions.deletePromotionSuccess(id, successMessage));
    }
  } catch (error) {
    yield put(CampaignPromotionActions.deletePromotionFailure(error));
  }
}

function* clonePromotionHandler({ payload: { id, name } }) {
  const clonedPromotion = yield call(CampaignPromotionsApi.cloneCampaignPromotion, { id, name });
  if (clonedPromotion.error) yield put(CampaignPromotionActions.clonePromotionFailure(clonedPromotion.error));
  else yield put(CampaignPromotionActions.clonePromotionSuccess(clonedPromotion));

  yield put(CampaignPromotionActions.toggleClonePromotionModal(false));
}

function* toggleClonePromotionModalHandler({ payload: { id } }) {
  if (id) {
    const promotion = yield select(CampaignPromotionsSelectors.campaignPromotionById, id);
    yield put(CampaignPromotionActions.setCloningPromotion(promotion));
  }
}

function* cancelEditPromotionHandler() {
  const isDirty = yield select(CampaignPromotionsSelectors.arePromotionsDirty);
  if (isDirty) {
    yield put(CampaignPromotionActions.setVisibilityDiscardChangesModal(true));
    yield put(CKForm.Actions.validateForm(FORM_ID));
  } else {
    yield put(CampaignPromotionActions.cancelPromotion());
  }
}

function* changePromotionDetailStrategyHandler() {
  const activePromotion = yield select(CampaignPromotionsSelectors.activePromotion);
  //if changing to Detailed promotion mode, set CTA to GO_TO_FORM
  yield put(
    CampaignPromotionActions.changePromotion(
      'ctaLink',
      activePromotion.detailStrategy.id === RefData.Enum.STRATEGY.PROMOTION_DETAILS.INLINE
        ? CTA_OPTIONS.GO_TO_FORM.value
        : ''
    )
  );
}

/**
 * This saga will look for state changes for actions happening in promotion editor.
 * When such actions happen, it will compare the promotion editor and promotion redux states, then it will invoke display apis to update a module or a whole page.
 */
function* promotionUpdateHandler() {
  let prevPromotionState;

  //check if Promotion Editor is visible and if it's showing live preview
  if (yield select(CampaignPromotionsSelectors.isEditing)) {
    //only execute the code when we are using the live preview mode
    const nextPromotionState = yield select(CampaignPromotionsSelectors.activePromotion);
    if (!_.isEqual(prevPromotionState, nextPromotionState)) {
      LivePreviewUtils.updatePromotionPreview({
        activePromotionId: nextPromotionState.id,
        promotions:        [nextPromotionState],
      });
      prevPromotionState = nextPromotionState;
    }
  }
}

export default function* CampaignPromotionsSaga() {
  yield all([
    takeLatest(({ type: actionType }) => {
      const actionName = (actionType && actionType.replace(/^\[\d+\]\s*/, '')) || ''; //action names created with react-act are getting a counter in their name similar to '[123] The real action name', so get rid of the counter, brackets and extra space
      return actionName.startsWith(ACTION_NAMESPACE); //only listening for promotion related actions
    }, promotionUpdateHandler),
  ]);

  yield all([takeLatest(CampaignPromotionActions.fetchPromotions.getType(), fetchPromotionsHandler)]);
  yield all([takeLatest(CampaignPromotionActions.openPromotionEditor.getType(), openPromotionEditorHandler)]);
  yield all([takeLatest(CampaignPromotionActions.editPromotionLayout.getType(), editPromotionLayoutHandler)]);
  yield all([
    takeLatest(CampaignPromotionActions.createPromotionFromTemplate.getType(), createPromotionFromTemplateHandler),
  ]);
  yield all([takeLatest(CampaignPromotionActions.publishPromotion.getType(), publishPromotionHandler)]);
  yield all([takeLatest(CampaignPromotionActions.draftPromotion.getType(), draftPromotionHandler)]);
  yield all([takeLatest(CampaignPromotionActions.clonePromotion.getType(), clonePromotionHandler)]);

  yield all([
    takeLatest(CampaignPromotionActions.changePromotionDetailStrategy.getType(), changePromotionDetailStrategyHandler),
  ]);

  yield all([takeLatest(CampaignPromotionActions.showConfirmDeleteMessage.getType(), showConfirmDeleteMessageHandler)]);
  yield all([
    takeLatest(CampaignPromotionActions.toggleClonePromotionModal.getType(), toggleClonePromotionModalHandler),
  ]);
  yield all([takeLatest(CampaignPromotionActions.persistPromotions.getType(), persistPromotionsHandler)]);
  yield all([takeLatest(CampaignPromotionActions.attemptEditPromotion.getType(), attemptEditPromotionHandler)]);
  yield all([takeLatest(CampaignPromotionActions.cancelEditPromotion.getType(), cancelEditPromotionHandler)]);
}
